import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { NSOrderFillConstants } from '@ruby/configs/order-fill.constants';
import { IOrderFillDetail } from '@ruby/modules/order-fill/model/order-fill.interface';
import { EOrderFillDateInterval } from '@ruby/shared/enums/order-fill.enums';
import { NSDateUtils } from '@ruby/shared/services/utils/ns-date.utils';

@Component({
  selector: 'ng-daily-chart',
  templateUrl: './daily-chart.component.html',
  styleUrls: ['./daily-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DailyChartComponent implements OnChanges {
  @Input() metrics: Array<IOrderFillDetail> = [];
  @Input() dateInterval!: { previous?: EOrderFillDateInterval; current?: EOrderFillDateInterval };
  @Output() returnNavigation: EventEmitter<void> = new EventEmitter();
  config!: Record<string, unknown>;
  isEmptyBarChart = true;
  lastWeek: EOrderFillDateInterval = EOrderFillDateInterval.LAST_WEEK;

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metrics) {
      this.config = this.setUpBarChart();
    }
  }

  private setUpBarChart(): Record<string, unknown> {
    const xAxisData: Array<string> = [];
    const requestedSeries: Array<number> = [];
    const onPremiseRejected: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.onPremiseRejected);
    const onPremise: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.onPremise);
    const filledEmptySpace: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.onPremiseRejected + detail.onPremise);
    const filledAfterSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.filledAfterCutoff);
    const filledBeforeSeries: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.filledBeforeCutoff);
    const releaseLoadedEmptySpace: Array<number> =
      this.metrics.map((detail: IOrderFillDetail) =>
        detail.onPremiseRejected + detail.onPremise + detail.filledAfterCutoff + detail.filledBeforeCutoff - detail.releaseLoaded);
    const releaseLoaded: Array<number> = this.metrics.map((detail: IOrderFillDetail) => detail.releaseLoaded);

    this.metrics.forEach((detail: IOrderFillDetail) => {
      xAxisData.push(`${ NSDateUtils.formatNSDate(detail.startDate, 'M/d') }`);
      requestedSeries.push(detail.requested);
    });

    this.isEmptyBarChart = [filledBeforeSeries, filledAfterSeries, requestedSeries, onPremiseRejected, onPremise, releaseLoadedEmptySpace]
      .every((data: Array<number>) => data.every((value: number) => value === 0));

    return {
      ...NSOrderFillConstants.dailyLineConfig,
      xAxis: {
        ...NSOrderFillConstants.dailyLineConfig.xAxis,
        data: xAxisData
      },
      yAxis: {
        ...NSOrderFillConstants.dailyLineConfig.yAxis
      },
      series: [
        {
          ...NSOrderFillConstants.dailyLineConfig.series[0],
          data: onPremiseRejected
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[1],
          data: onPremise
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[2],
          data: filledEmptySpace
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[3],
          data: filledAfterSeries
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[4],
          data: filledBeforeSeries
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[5],
          data: releaseLoadedEmptySpace
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[6],
          data: releaseLoaded
        },
        {
          ...NSOrderFillConstants.dailyLineConfig.series[7],
          data: requestedSeries
        }
      ]
    };
  }
}
